import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getVenue, updateVenue, getTrendyolgoStores, setTrendyolgoListening, getTrendyolgoRecentOrders, acceptTrendyolgoOrder, prepareTrendyolgoOrder, cancelTrendyolgoOrder } from "@/api/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Save, RefreshCw, AlertCircle, CheckCircle2, Webhook, Copy, Check, Radio, Clock, Printer, Braces } from "lucide-react";
import { printOrderReceipt, printTestReceipt } from "@/utils/print-receipt";
import type { Order } from "@/types";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3004";

const STATUS_TR: Record<string, string> = {
  pending: "Yeni",
  preparing: "Hazırlanıyor",
  ready: "Hazır",
  delivered: "Tamamlandı",
};

/**
 * Trendyol GO entegrasyon sayfası — beta.
 *
 * Admin paneli üzerinden `venue.trendyolgoEnabled` true set edilmiş
 * restoranlarda dashboard sidebar'ında "Trendyol GO" linki belirir.
 *
 * İki bölüm:
 *  1. Credentials formu — restoran sahibi GO satıcı panelindeki "Entegrasyon
 *     Bilgileri"nden Satıcı ID + API Key + API Secret'ı kaydeder
 *  2. "Siparişleri Çek" — backend'e `GET /trendyolgo/orders` çağrısı
 */
export function TrendyolgoPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const qc = useQueryClient();

  const { data: venue, isLoading } = useQuery({
    queryKey: ["venue", venueId],
    queryFn: () => getVenue(venueId!),
    enabled: !!venueId,
  });

  const [sellerId, setSellerId] = useState("");
  const [integrationRefCode, setIntegrationRefCode] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [token, setToken] = useState("");
  const [saveOk, setSaveOk] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  // Siparişin tüm datasını (externalRaw dahil) JSON olarak gösteren popup.
  const [jsonOrder, setJsonOrder] = useState<Order | null>(null);
  const [jsonCopied, setJsonCopied] = useState(false);
  // Yeni sipariş gelince otomatik termal fiş baskısı (tarayıcı). localStorage'da
  // saklanır; "0" = kapalı, diğer/eksik = açık (varsayılan açık).
  const [autoPrint, setAutoPrint] = useState(() => localStorage.getItem("trendyolgo-autoprint") !== "0");
  const toggleAutoPrint = (v: boolean) => {
    setAutoPrint(v);
    localStorage.setItem("trendyolgo-autoprint", v ? "1" : "0");
  };

  const webhookUrl = `${API_BASE}/api/webhooks/trendyolgo?venueId=${venueId}`;

  useEffect(() => {
    if (venue?.trendyolgoConfig) {
      setSellerId(venue.trendyolgoConfig.sellerId ?? "");
      setIntegrationRefCode(venue.trendyolgoConfig.integrationRefCode ?? "");
      setApiKey(venue.trendyolgoConfig.apiKey ?? "");
      setApiSecret(venue.trendyolgoConfig.apiSecret ?? "");
      setToken(venue.trendyolgoConfig.token ?? "");
    }
  }, [venue]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateVenue(venueId!, {
        // Cast — Venue tipi trendyolgoConfig'i taşır ama updateVenue
        // signature genel Partial<Venue>. Backend Zod schema doğrular.
        trendyolgoConfig: { sellerId, integrationRefCode, apiKey, apiSecret, token },
      } as Parameters<typeof updateVenue>[1]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["venue", venueId] });
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2500);
    },
  });

  const storesQuery = useQuery({
    queryKey: ["trendyolgo-stores", venueId],
    queryFn: () => getTrendyolgoStores(venueId!),
    enabled: false, // Sadece "Çek" butonuna basınca tetiklenir
    retry: false,
  });

  const listening = !!venue?.trendyolgoListening;

  const listeningMutation = useMutation({
    mutationFn: (enabled: boolean) => setTrendyolgoListening(venueId!, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["venue", venueId] }),
  });
  const listeningErr = listeningMutation.error as { response?: { data?: { message?: string; error?: string } } } | null;
  const listeningErrMsg = listeningErr?.response?.data?.message || listeningErr?.response?.data?.error;

  // Gelen siparişler — dinleme açıkken ~8sn'de bir tazelenir (canlı liste).
  const ordersQuery = useQuery({
    queryKey: ["trendyolgo-recent-orders", venueId],
    queryFn: () => getTrendyolgoRecentOrders(venueId!),
    enabled: !!venueId,
    refetchInterval: listening ? 8000 : false,
  });

  // Sipariş aksiyonları (Kabul/Hazır/İptal → Trendyol'a bildir). actingId =
  // o an işlem yapılan sipariş; buton bazlı spinner ve hata için tutulur.
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const actionMutation = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: "accept" | "prepared" | "cancel" }) => {
      if (kind === "accept") return acceptTrendyolgoOrder(venueId!, id);
      if (kind === "prepared") return prepareTrendyolgoOrder(venueId!, id);
      return cancelTrendyolgoOrder(venueId!, id);
    },
    onMutate: ({ id }) => { setActingId(id); setActionErr(null); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trendyolgo-recent-orders", venueId] }),
    onError: (err) => {
      const e = err as { response?: { data?: { message?: string; error?: string } } };
      setActionErr(e.response?.data?.message || e.response?.data?.error || "İşlem başarısız.");
    },
    onSettled: () => setActingId(null),
  });

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">Yükleniyor…</div>;
  }

  if (!venue?.trendyolgoEnabled) {
    return (
      <div className="max-w-md mx-auto py-12">
        <Card>
          <CardContent className="pt-6 text-center">
            <AlertCircle className="mx-auto mb-3 size-10 text-amber-500" />
            <p className="font-semibold">Trendyol GO entegrasyonu kapalı</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Bu işletme için Trendyol GO henüz açık değil. HereMenu yöneticisiyle iletişime geçin.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasCreds = !!(venue?.trendyolgoConfig?.sellerId && venue?.trendyolgoConfig?.apiKey && venue?.trendyolgoConfig?.apiSecret);
  const fetchErr = storesQuery.error as { response?: { data?: { error?: string; message?: string; status?: number } }; message?: string } | null;
  const fetchErrStatus = fetchErr?.response?.data?.status;
  const fetchErrMsg = fetchErr?.response?.data?.message
    || fetchErr?.response?.data?.error
    || fetchErr?.message;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Trendyol GO</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Trendyol GO satıcı panelindeki "Entegrasyon Bilgileri"nden aldığınız bilgilerle bağlantı kurun.
        </p>
      </div>

      {/* Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bağlantı Bilgileri</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Trendyol satıcı paneli → Mağaza adınız → Hesap Bilgilerim → Entegrasyon Bilgileri'ndeki 5 alanı buraya kopyalayın.
          </p>
          <div>
            <Label>Satıcı ID (Cari ID)</Label>
            <Input
              value={sellerId}
              onChange={(e) => setSellerId(e.target.value)}
              placeholder="örn. 6726318"
              autoComplete="off"
            />
          </div>
          <div>
            <Label>Entegrasyon Referans Kodu</Label>
            <Input
              type="password"
              value={integrationRefCode}
              onChange={(e) => setIntegrationRefCode(e.target.value)}
              placeholder="Entegrasyon Referans Kodu"
              autoComplete="off"
            />
          </div>
          <div>
            <Label>API Key</Label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="API Key"
              autoComplete="off"
            />
          </div>
          <div>
            <Label>API Secret</Label>
            <Input
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder="API Secret"
              autoComplete="off"
            />
          </div>
          <div>
            <Label>Token</Label>
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Token"
              autoComplete="off"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !sellerId || !apiKey || !apiSecret}>
              <Save className="mr-2 size-4" />
              {saveMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
            </Button>
            {saveOk && (
              <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
                <CheckCircle2 className="size-4" /> Kaydedildi
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sipariş dinleme (polling) aç/kapat */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Radio className="size-4" />
            Sipariş Dinleme
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="pr-3">
              <p className="text-sm font-medium">
                {listening ? "🟢 Dinleniyor" : "⚪ Kapalı"}
              </p>
              <p className="text-xs text-muted-foreground">
                {listening
                  ? "Yeni siparişler otomatik çekiliyor; aşağıda ve Mutfak (KDS) ekranında görünür."
                  : "Açınca Trendyol siparişleri periyodik çekilip buraya ve Mutfak'a düşer."}
              </p>
            </div>
            <Switch
              checked={listening}
              disabled={listeningMutation.isPending || !hasCreds}
              onCheckedChange={(v) => listeningMutation.mutate(v)}
            />
          </div>
          {!hasCreds && (
            <p className="text-xs text-muted-foreground">Önce yukarıdaki bağlantı bilgilerini kaydedin.</p>
          )}
          {listeningErrMsg && <p className="text-sm text-destructive">{listeningErrMsg}</p>}
        </CardContent>
      </Card>

      {/* Otomatik fiş yazdırma */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Printer className="size-4" />
            Otomatik Fiş Yazdırma
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="pr-3">
              <p className="text-sm font-medium">{autoPrint ? "🖨️ Açık" : "⚪ Kapalı"}</p>
              <p className="text-xs text-muted-foreground">
                Açıkken yeni Trendyol GO siparişi geldiğinde tarayıcı otomatik termal fiş (80mm) basar.
                Bu cihaz/tarayıcı için geçerlidir.
              </p>
            </div>
            <Switch checked={autoPrint} onCheckedChange={toggleAutoPrint} />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => printTestReceipt()}>
              <Printer className="mr-2 size-4" />
              Test Fişi Yazdır
            </Button>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
            <span className="font-medium">Penceresiz (tam otomatik) baskı için:</span> Chrome'u
            <code className="mx-1 rounded bg-amber-100 px-1 dark:bg-amber-900/40">--kiosk-printing</code>
            bayrağıyla açın ve termal yazıcıyı <span className="font-medium">varsayılan yazıcı</span> yapın.
            Bu mod yoksa her siparişte tek seferlik yazdırma penceresi açılır.
          </div>
        </CardContent>
      </Card>

      {/* Gelen siparişler — canlı liste */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Gelen Siparişler</CardTitle>
          <div className="flex items-center gap-3">
            {listening && (
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <span className="size-2 animate-pulse rounded-full bg-emerald-500" />Canlı
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => ordersQuery.refetch()} disabled={ordersQuery.isFetching}>
              <RefreshCw className={`mr-2 size-4 ${ordersQuery.isFetching ? "animate-spin" : ""}`} />
              Yenile
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {actionErr && (
            <div className="mb-3 rounded-md border border-destructive/50 bg-destructive/5 p-2 text-sm text-destructive">
              {actionErr}
            </div>
          )}
          {(ordersQuery.data?.length ?? 0) === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {listening
                ? "Henüz sipariş yok. Yeni sipariş gelince burada belirir."
                : "Dinleme kapalı. Açtığında siparişler burada listelenecek."}
            </p>
          ) : (
            <div className="space-y-2">
              {ordersQuery.data!.map((o) => (
                <div key={o.id || o._id} className="rounded-lg border p-3">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{o.table}</span>
                      <Badge variant="secondary">{STATUS_TR[o.status] || o.status}</Badge>
                    </div>
                    <span className="text-sm font-bold text-primary">
                      {o.totalPrice.toLocaleString("tr-TR")} ₺
                    </span>
                  </div>
                  <ul className="text-xs text-muted-foreground">
                    {o.items.map((it, i) => (
                      <li key={i}>{it.quantity}× {it.name}</li>
                    ))}
                  </ul>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Clock className="size-3" />
                      {new Date(o.createdAt).toLocaleString("tr-TR")}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setJsonOrder(o)}
                      >
                        <Braces className="mr-1 size-3.5" />
                        JSON
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => printOrderReceipt(o)}
                      >
                        <Printer className="mr-1 size-3.5" />
                        Yazdır
                      </Button>
                    </div>
                  </div>

                  {(o.status === "pending" || o.status === "preparing") && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {o.status === "pending" && (
                        <Button
                          size="sm"
                          disabled={actionMutation.isPending && actingId === (o.id || o._id)}
                          onClick={() => actionMutation.mutate({ id: o.id || o._id!, kind: "accept" })}
                        >
                          {actionMutation.isPending && actingId === (o.id || o._id) ? <Loader2 className="mr-1 size-4 animate-spin" /> : null}
                          Kabul Et
                        </Button>
                      )}
                      {o.status === "preparing" && (
                        <Button
                          size="sm"
                          disabled={actionMutation.isPending && actingId === (o.id || o._id)}
                          onClick={() => actionMutation.mutate({ id: o.id || o._id!, kind: "prepared" })}
                        >
                          {actionMutation.isPending && actingId === (o.id || o._id) ? <Loader2 className="mr-1 size-4 animate-spin" /> : null}
                          Hazırlandı
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actionMutation.isPending && actingId === (o.id || o._id)}
                        onClick={() => { if (confirm("Siparişi iptal etmek istediğinize emin misiniz?")) actionMutation.mutate({ id: o.id || o._id!, kind: "cancel" }); }}
                      >
                        İptal
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhook — manuel kayıt bilgileri */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Webhook className="size-4" />
            Otomatik Sipariş (Webhook)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Trendyol GO Yemek'te webhook kaydı <span className="font-medium">manuel</span> yapılır. Aşağıdaki
            adresi Trendyol'a (satıcı bildirimi ile) ileterek tanımlatın; tanımlandıktan sonra yeni siparişler
            doğrudan mutfak (KDS) ekranınıza düşer.
          </p>

          <div>
            <p className="mb-1 text-xs text-muted-foreground">Webhook URL'i (POST, Basic Auth)</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md bg-muted px-2 py-1.5 text-xs">{webhookUrl}</code>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await navigator.clipboard.writeText(webhookUrl);
                  setUrlCopied(true);
                  setTimeout(() => setUrlCopied(false), 1500);
                }}
              >
                {urlCopied ? <Check className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
            Kimlik doğrulama <span className="font-medium">Basic Auth</span> iledir. Trendyol'a iletilecek
            kullanıcı adı ve parola bilgilerini HereMenu ekibi sağlar (sunucuda güvenle saklanır).
            Kayıt önce Stage, ardından Production ortamında yapılır.
          </div>
        </CardContent>
      </Card>

      {/* Bağlantı testi — restoran bilgileri */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Bağlantı Testi (Restoran Bilgileri)</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => storesQuery.refetch()}
            disabled={storesQuery.isFetching || !hasCreds}
          >
            {storesQuery.isFetching
              ? <><Loader2 className="mr-2 size-4 animate-spin" />Çekiliyor…</>
              : <><RefreshCw className="mr-2 size-4" />Çek</>}
          </Button>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-xs text-muted-foreground">
            Trendyol'dan mağaza/restoran bilgilerini çeker. Başarılıysa Satıcı ID, API Key ve API Secret doğru demektir.
          </p>
          {!hasCreds && (
            <p className="text-sm text-muted-foreground">Önce Satıcı ID, API Key ve API Secret kaydedin.</p>
          )}
          {fetchErrMsg && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
              <p className="font-medium">Bağlantı hatası{fetchErrStatus ? ` (HTTP ${fetchErrStatus})` : ""}</p>
              <p className="mt-1 break-words">{fetchErrMsg}</p>
            </div>
          )}
          {storesQuery.data && (
            <div className="space-y-2">
              <p className="text-sm text-emerald-600">
                Bağlantı başarılı — {storesQuery.data.restaurants.length} restoran bulundu.
              </p>
              <pre className="max-h-96 overflow-auto rounded-md border bg-muted/30 p-3 text-xs">
                {JSON.stringify(storesQuery.data.restaurants, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sipariş ham datası (JSON) — ödeme/not/müşteri alan adlarını bulmak için */}
      <Dialog open={!!jsonOrder} onOpenChange={(open) => { if (!open) setJsonOrder(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 pr-6">
              <span>Sipariş Datası (JSON)</span>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await navigator.clipboard.writeText(JSON.stringify(jsonOrder, null, 2));
                  setJsonCopied(true);
                  setTimeout(() => setJsonCopied(false), 1500);
                }}
              >
                {jsonCopied ? <Check className="mr-1 size-4 text-emerald-600" /> : <Copy className="mr-1 size-4" />}
                Kopyala
              </Button>
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">externalRaw</span> alanı Trendyol'dan gelen ham payload'dur — ödeme yöntemi,
            sipariş notu ve müşteri bilgisinin gerçek alan adları orada. Bu datayı kopyalayıp gönderin.
          </p>
          <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted/30 p-3 text-xs leading-relaxed">
            {jsonOrder ? JSON.stringify(jsonOrder, null, 2) : ""}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
